import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Boxes, GitBranch, X, ArrowRight, SprayCan, CheckCircle2 } from 'lucide-react';
import LotSprayHistory from '../../components/nursery/LotSprayHistory';
import LotStageDialog, { type LotChangeMode } from '../../components/nursery/LotStageDialog';
import LotPlaceDialog from '../../components/nursery/LotPlaceDialog';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import { sprayLinkFor } from '../../utils/nurserySprayTargets';
import nurseryService from '../../services/nursery';
import { downloadBlob } from '../../utils/downloadBlob';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '../../utils/timezone';
import { GRADE_ORDINAL } from '../../utils/treeTypeGrades';
import { STOCKTAKE_KIND_NAME } from '../../types/nurseryStocktake';
import colourService from '../../services/nurseryColour';
import LotBands from '../../components/nursery/LotBands';
import type { LotBand } from '../../types/nurseryColour';
import { slotWhere, compareSlots } from '../../utils/nurseryRowSlots';
import { destinationItems, eventFits, methodsFor } from '../../utils/propagation';
import nurseryPropagationService from '../../services/nurseryPropagation';
import type {
  NurseryLot, NurseryLotLineage, NurseryLocation, MovementEventType, ApplyMovementPayload, LotStage, NurseryStockItem,
} from '../../types/nursery';
import type { ContainerType, PropagationMethod } from '../../types/nurseryPropagation';
import { LOT_STAGE_LABELS } from '../../types/nursery';

const stageName = (s: string | null | undefined) => (s ? LOT_STAGE_LABELS[s as LotStage] ?? s : '—');

/** A stock item in words: "Gala / M.9 · Bare root MS1". */
const itemName = (i: NurseryStockItem) =>
  `${[i.cultivar_name, i.rootstock_name].filter(Boolean).join(' / ')} · ${i.tree_type_name ?? i.tree_type_code ?? ''}`;

/**
 * makes: the movement can make another stock item (the destination picker).
 * containers: the new lot can be counted in trays or flasks.
 */
const EVENTS: {
  value: MovementEventType; label: string; needsDestination?: boolean; terminal?: boolean; grade?: boolean;
  makes?: boolean; containers?: boolean;
}[] = [
  { value: 'transplant', label: 'Transplant (move to new location)', needsDestination: true, makes: true, containers: true },
  { value: 'pot_up', label: 'Pot up', needsDestination: true, makes: true, containers: true },
  { value: 'root', label: 'Rooted or germinated (count what took)' },
  { value: 'deflask', label: 'Deflask (plantlets out of the flask)', needsDestination: true, makes: true, containers: true },
  { value: 'harvest', label: 'Harvest layers or divisions (the mother stays as it is)', needsDestination: true, makes: true, containers: true },
  { value: 'graft', label: 'Graft', needsDestination: true, makes: true },
  { value: 'bud', label: 'Bud', needsDestination: true, makes: true },
  { value: 'split', label: 'Split', needsDestination: true, containers: true },
  { value: 'grade', label: 'Grade (record counts)', grade: true },
  { value: 'dig', label: 'Dig (lift bareroot)', terminal: true },
  { value: 'ship', label: 'Ship', terminal: true },
  { value: 'sell', label: 'Sell', terminal: true },
  { value: 'cull', label: 'Cull', terminal: true },
  { value: 'adjust', label: 'Adjust count', grade: false },
];

const todayStr = () => new Date().toISOString().slice(0, 10);


/** A cost to the cent, or to four places when it was bought at $2.593. */
function formatUnitCost(v: string | number): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function LotDetail() {
  const { id } = useParams<{ id: string }>();
  const lotId = Number(id);
  const orgTz = useOrgTimezone();
  const navigate = useNavigate();
  // Nursery spraying is the spray module's: planning one takes a spray
  // creator or admin seat, and opening one any spray seat.
  const { hasAccess: canPlanSpray } = useModuleLicense('spray', ['creator', 'admin']);
  const { hasAccess: canOpenSprays } = useModuleLicense('spray', ['operator', 'creator', 'admin']);
  // Moving a lot along is field work; taking it out of stock is a manager's.
  const { hasAccess: canOperate } = useStockSeat('operate');
  const { hasAccess: canManage } = useStockSeat('manage');
  const [change, setChange] = useState<{ mode: LotChangeMode; stage?: LotStage } | null>(null);
  const [placing, setPlacing] = useState(false);

  const [lot, setLot] = useState<NurseryLot | null>(null);
  const [lineage, setLineage] = useState<NurseryLotLineage | null>(null);
  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showMove, setShowMove] = useState(false);
  const [mvEvent, setMvEvent] = useState<MovementEventType>('transplant');
  const [mvQty, setMvQty] = useState<number | ''>('');
  const [mvDate, setMvDate] = useState(todayStr());
  const [mvLocation, setMvLocation] = useState<number | ''>('');
  const [mvNotes, setMvNotes] = useState('');
  const [mvSource, setMvSource] = useState('');
  const [mvFirst, setMvFirst] = useState<number | ''>('');
  const [mvSecond, setMvSecond] = useState<number | ''>('');
  const [mvCull, setMvCull] = useState<number | ''>('');
  // Propagation: what the movement makes, how, and in what.
  const [mvItem, setMvItem] = useState<number | ''>('');
  const [mvMethod, setMvMethod] = useState<number | ''>('');
  const [mvContainer, setMvContainer] = useState<number | ''>('');
  const [mvContainerCount, setMvContainerCount] = useState<number | ''>('');
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  const [methods, setMethods] = useState<PropagationMethod[]>([]);
  const [containers, setContainers] = useState<ContainerType[]>([]);
  const [motherError, setMotherError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // The paint bands on the trees, when the nursery colour-codes.
  const [bands, setBands] = useState<LotBand[]>([]);
  useEffect(() => {
    let live = true;
    colourService.bands([lotId])
      .then((res) => { if (live && res.success && res.data.enabled) setBands(res.data.lots[lotId] ?? []); })
      .catch(() => { /* no colours */ });
    return () => { live = false; };
  }, [lotId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lotRes, lineageRes, locRes] = await Promise.all([
        nurseryService.getLot(lotId),
        nurseryService.getLotLineage(lotId),
        nurseryService.getLocations({ active: true }),
      ]);
      if (lotRes.success) setLot(lotRes.data || null);
      else setError('Lot not found');
      if (lineageRes.success) setLineage(lineageRes.data || null);
      if (locRes.success) setLocations(locRes.data || []);
      // The movement form's choices. None is needed to read the lot, so a
      // failure here leaves them empty rather than the page broken.
      const [items, meth, cont] = await Promise.allSettled([
        nurseryService.getStockItems({ active: true }),
        nurseryPropagationService.getMethods(),
        nurseryPropagationService.getContainerTypes(),
      ]);
      if (items.status === 'fulfilled' && items.value.success) setStockItems(items.value.data || []);
      if (meth.status === 'fulfilled' && meth.value.success) setMethods(meth.value.data || []);
      if (cont.status === 'fulfilled' && cont.value.success) setContainers(cont.value.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load lot');
    } finally {
      setLoading(false);
    }
  }, [lotId]);

  useEffect(() => { load(); }, [load]);

  const eventMeta = EVENTS.find(e => e.value === mvEvent)!;
  // Walk order, so the two sides of a track sit together, and never the place
  // the lot already stands in.
  const destinations = locations
    .filter(l => l.id !== lot?.location_id)
    .sort((a, b) => compareSlots({ ...a, location_name: a.name }, { ...b, location_name: b.name }));

  const makesOptions = lot ? destinationItems(mvEvent, lot, stockItems) : [];
  const methodOptions = methodsFor(mvEvent, methods);
  const chosenContainer = containers.find(c => c.id === mvContainer);

  const submitMove = async () => {
    if (mvQty === '' && mvEvent !== 'grade') { setSaveError('Quantity is required'); return; }
    // Budding or grafting makes a new tree: it must say which.
    if ((mvEvent === 'bud' || mvEvent === 'graft') && mvItem === '') {
      setSaveError(`Choose what it is ${mvEvent === 'bud' ? 'budded' : 'grafted'} to`); return;
    }
    // A transplant that names no new place splits the lot where it stands,
    // which reads as trees added with none taken away.
    if (mvEvent === 'transplant' && (mvLocation === '' || mvLocation === lot?.location_id)) {
      setSaveError('Choose where the trees are going'); return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: ApplyMovementPayload = {
        event_type: mvEvent,
        from_lot_id: lotId,
        quantity: mvEvent === 'grade' ? 0 : Number(mvQty || 0),
        movement_date: mvDate,
        notes: mvNotes || null,
      };
      if (eventMeta.needsDestination && mvLocation) payload.to_location_id = Number(mvLocation);
      if (eventMeta.makes && mvItem !== '') payload.to_stock_item_id = Number(mvItem);
      if (mvMethod !== '') payload.propagation_method_id = Number(mvMethod);
      if (eventMeta.containers && mvContainer !== '') {
        payload.container_type_id = Number(mvContainer);
        if (mvContainerCount !== '') payload.container_count = Number(mvContainerCount);
      }
      if (mvSource) payload.source_supplier = mvSource;
      if (mvEvent === 'grade') {
        payload.grading = {
          first_grade_count: mvFirst === '' ? 0 : Number(mvFirst),
          second_grade_count: mvSecond === '' ? 0 : Number(mvSecond),
          cull_count: mvCull === '' ? 0 : Number(mvCull),
          count_before: lot?.current_count,
        };
      }
      const res = await nurseryService.applyMovement(payload);
      if (res.success) {
        setShowMove(false);
        resetMove();
        load();
      } else {
        setSaveError((res as { error?: { message?: string } }).error?.message || 'Failed to record movement');
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setSaving(false);
    }
  };

  const resetMove = () => {
    setMvEvent('transplant'); setMvQty(''); setMvDate(todayStr()); setMvLocation('');
    setMvNotes(''); setMvSource(''); setMvFirst(''); setMvSecond(''); setMvCull(''); setSaveError(null);
    setMvItem(''); setMvMethod(''); setMvContainer(''); setMvContainerCount('');
  };

  const chooseEvent = (e: MovementEventType) => {
    setMvEvent(e);
    // What one movement makes is rarely what another does.
    setMvItem(''); setMvMethod(''); setSaveError(null);
  };

  // A mother (stool bed, stock plants) is never ready stock, and material is
  // taken from it without reducing it.
  const setMother = async (isMother: boolean) => {
    if (!lot) return;
    setMotherError(null);
    try {
      const res = await nurseryService.updateLot(lot.id, { is_mother: isMother });
      if (res.success) load();
      else setMotherError((res as { error?: { message?: string } }).error?.message || 'Could not change it');
    } catch (e) {
      setMotherError(e instanceof Error ? e.message : 'Could not change it');
    }
  };

  const readyable = !!lot && canOperate && lot.status === 'active' && lot.current_stage !== 'ready'
    && lot.current_count > 0 && !lot.is_mother;

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded-lg animate-pulse" /></div>;
  if (error || !lot) return <div className="max-w-4xl mx-auto px-4 py-6"><div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error || 'Lot not found'}</div></div>;

  return (
    <>
      <PageHeader
        title={`${lot.cultivar_name}${lot.rootstock_name ? ' / ' + lot.rootstock_name : ''}`}
        subtitle={`${lot.tree_type_code} · ${stageName(lot.current_stage)} · ${lot.current_count.toLocaleString()} trees`}
        icon={<Boxes className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                downloadBlob(await nurseryService.getLotLabelsPdf([lot.id]), `lot-label-${lot.lot_code || lot.id}.pdf`);
              } catch { /* the download simply does not happen; nothing else depends on it */ }
            }}>
              Print label
            </Button>
            {canPlanSpray && lot.location_id != null && lot.status === 'active' && (
              <Button variant="outline" size="sm" onClick={() => {
                const here = locations.find(l => l.id === lot.location_id);
                navigate(sprayLinkFor({
                  farmId: lot.farm_id ?? here?.farm_id ?? null,
                  locationIds: [lot.location_id as number],
                }));
              }}>
                <SprayCan className="h-4 w-4" /> Spray
              </Button>
            )}
            <Button variant={readyable ? 'outline' : 'primary'} size="sm" onClick={() => setShowMove(true)} disabled={lot.status !== 'active'}>
              Record movement
            </Button>
            {readyable && (
              <Button variant="success" size="sm" onClick={() => setChange({ mode: 'stage', stage: 'ready' })}>
                <CheckCircle2 className="h-4 w-4" /> Ready to sell
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Facts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
            <div className="text-xs text-neutral-400">Stage</div>
            <div className={lot.current_stage === 'ready' ? 'text-sm font-medium text-green-700' : 'text-sm font-medium text-neutral-800'}>
              {stageName(lot.current_stage)}
            </div>
            {canOperate && lot.status === 'active' && (
              <button type="button" className="text-xs text-primary-600 hover:underline" onClick={() => setChange({ mode: 'stage' })}>
                Change stage
              </button>
            )}
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
            <div className="text-xs text-neutral-400">Status</div>
            <div className="text-sm font-medium text-neutral-800 capitalize">{lot.status}</div>
            {canManage && lot.status === 'active' && (
              <button type="button" className="text-xs text-primary-600 hover:underline" onClick={() => setChange({ mode: 'archive' })}>
                Take out of stock
              </button>
            )}
            {canManage && lot.status === 'archived' && (
              <button type="button" className="text-xs text-primary-600 hover:underline" onClick={() => setChange({ mode: 'restore' })}>
                Put back in stock
              </button>
            )}
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
            <div className="text-xs text-neutral-400">Location</div>
            <div className={lot.location_id ? 'text-sm font-medium text-neutral-800' : 'text-sm font-medium text-amber-700'}>
              {lot.location_name || 'Not set'}
            </div>
            {canManage && lot.status === 'active' && (
              <button type="button" className="text-xs text-primary-600 hover:underline" onClick={() => setPlacing(true)}>
                {lot.location_id ? 'Change place' : 'Set place'}
              </button>
            )}
          </div>
          {[
            ['Ready', lot.ready_date ? formatLocalDateInTz(lot.ready_date, orgTz) : '—'],
            ['Count', lot.current_count.toLocaleString()],
            ['Initial', lot.initial_count.toLocaleString()],
            // The best count feeds the forecast; a graded lot has a grade instead.
            lot.grade_rank
              ? ['Grade', `${GRADE_ORDINAL[lot.grade_rank - 1] ?? lot.grade_rank} grade`]
              : ['Best count', lot.last_count_kind
                ? `${STOCKTAKE_KIND_NAME[lot.last_count_kind]}${lot.last_counted_at ? `, ${formatDateInTz(lot.last_counted_at, orgTz)}` : ''}`
                : 'Not counted'],
            ['Block', lot.block_name || '—'],
            ['Farm', lot.farm_name || '—'],
            // Bought in: what one cost (ex GST), when, and from whom.
            ...(lot.unit_cost != null ? [['Cost each (ex GST)', formatUnitCost(lot.unit_cost)]] : []),
            ...(lot.received_on ? [['Received', formatLocalDateInTz(lot.received_on.slice(0, 10), orgTz)]] : []),
            ...(lot.supplier_name ? [['Supplier', lot.supplier_name]] : []),
            ...(lot.container_type_name
              ? [['In', `${lot.container_count ?? '?'} × ${lot.container_type_name}`]]
              : []),
          ].map(([k, v]) => (
            <div key={k} className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <div className="text-xs text-neutral-400">{k}</div>
              <div className="text-sm font-medium text-neutral-800">{v}</div>
            </div>
          ))}
          {bands.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <div className="text-xs text-neutral-400">Paint</div>
              <LotBands bands={bands} />
            </div>
          )}
        </div>

        {(lot.is_mother || (canManage && lot.status === 'active')) && (
          <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-neutral-800">
                  {lot.is_mother ? 'Mother plant' : 'Not a mother plant'}
                </div>
                <p className="text-xs text-neutral-500">
                  {lot.is_mother
                    ? 'Layers, cuttings and wood are taken from it without reducing its count. It is never ready stock.'
                    : 'A stool bed or stock plants that material is taken from.'}
                </p>
              </div>
              {canManage && lot.status === 'active' && (lot.is_mother || lot.current_stage !== 'ready') && (
                <Button variant="outline" size="sm" onClick={() => setMother(!lot.is_mother)}>
                  {lot.is_mother ? 'Not a mother plant' : 'Mark as mother plant'}
                </Button>
              )}
            </div>
            {motherError && <p className="text-xs text-red-600 mt-1">{motherError}</p>}
          </div>
        )}

        <div className="mb-6">
          <LotSprayHistory lotId={lot.id} canOpenSprays={canOpenSprays} />
        </div>

        {/* Lineage */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <GitBranch className="h-5 w-5 text-primary-600" />
            <h3 className="text-sm font-semibold text-neutral-800">Lineage &amp; movements</h3>
          </div>
          {!lineage || lineage.movements.length === 0 ? (
            <p className="text-sm text-neutral-400">No movements recorded yet.</p>
          ) : (
            <ol className="relative border-l border-neutral-200 ml-2 space-y-4">
              {lineage.movements.map(m => {
                const fromLot = lineage.lots.find(l => l.id === m.from_lot_id);
                const toLot = lineage.lots.find(l => l.id === m.to_lot_id);
                return (
                  <li key={m.id} className="ml-4">
                    <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-primary-500 border-2 border-white" />
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-neutral-800 capitalize">
                        {m.event_type === 'stage' || m.event_type === 'root' ? `${stageName(m.from_stage)} → ${stageName(m.to_stage)}`
                          : m.event_type === 'archive' ? 'Taken out of stock'
                          : m.event_type === 'restore' ? 'Put back in stock'
                          : m.event_type.replace('_', ' ')}
                      </span>
                      <span className="text-neutral-400">{formatLocalDateInTz(m.movement_date, orgTz)}</span>
                      {m.quantity > 0 && <span className="text-neutral-600">· {m.quantity.toLocaleString()}</span>}
                    </div>
                    {/* A change made to the lot where it stands goes nowhere. */}
                    {m.from_lot_id !== m.to_lot_id && <div className="flex items-center gap-2 text-xs text-neutral-500 mt-0.5">
                      {fromLot ? <span>Lot #{fromLot.id} {fromLot.cultivar_name}</span> : <span>{m.source_supplier || 'External'}</span>}
                      <ArrowRight className="h-3 w-3" />
                      {toLot ? <span>Lot #{toLot.id}{toLot.location_name ? ` · ${toLot.location_name}` : ''}</span> : <span>—</span>}
                    </div>}
                    {m.notes && <div className="text-xs text-neutral-400 mt-0.5">{m.notes}</div>}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {placing && (
          <LotPlaceDialog lot={lot} locations={locations} onClose={() => setPlacing(false)} onDone={() => load()} />
        )}

        {change && (
          <LotStageDialog
            lots={[lot]}
            mode={change.mode}
            initialStage={change.stage}
            onClose={() => setChange(null)}
            onDone={() => load()}
          />
        )}

        {/* Movement modal */}
        {showMove && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            {/* Scrolls inside a short screen, so its buttons are always reachable. */}
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-full overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-neutral-800">Record movement</h3>
                <button onClick={() => { setShowMove(false); resetMove(); }}><X className="h-5 w-5 text-neutral-400 hover:text-neutral-600" /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="mv-event" className="block text-sm font-medium text-neutral-700 mb-1">Event</label>
                  <select id="mv-event" value={mvEvent} onChange={e => chooseEvent(e.target.value as MovementEventType)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                    {EVENTS.filter(e => eventFits(e.value, lot)).map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {mvEvent !== 'grade' && (
                    <div>
                      <label htmlFor="mv-qty" className="block text-sm font-medium text-neutral-700 mb-1">
                        {mvEvent === 'adjust' ? 'New count' : mvEvent === 'root' ? 'Count that took' : 'Quantity'}
                      </label>
                      <input id="mv-qty" type="number" min="0" value={mvQty}
                        onChange={e => setMvQty(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  )}
                  <div>
                    <label htmlFor="mv-date" className="block text-sm font-medium text-neutral-700 mb-1">Date</label>
                    <input id="mv-date" type="date" value={mvDate} onChange={e => setMvDate(e.target.value)}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>

                {eventMeta.needsDestination && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Destination location</label>
                    <select value={mvLocation} onChange={e => setMvLocation(e.target.value ? Number(e.target.value) : '')}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                      <option value="">{mvEvent === 'transplant' ? 'Choose a place…' : 'Where it stands now'}</option>
                      {destinations.map(l => (
                        <option key={l.id} value={l.id}>
                          {l.location_type === 'nursery_row' && l.row_number != null
                            ? slotWhere({ ...l, location_name: l.name })
                            : [l.block_name, l.name].filter(Boolean).join(' · ')}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-neutral-400 mt-1">
                      {mvEvent === 'harvest'
                        ? `Starts a new lot of ${mvQty || 0} at the destination. The mother keeps its ${lot.current_count.toLocaleString()}.`
                        : <>Takes {mvQty || 0} from this lot{lot.location_name ? ` at ${lot.location_name}` : ''}, leaving {Math.max(lot.current_count - Number(mvQty || 0), 0).toLocaleString()}, and starts a new lot at the destination.</>}
                    </p>
                  </div>
                )}

                {eventMeta.makes && (
                  <div>
                    <label htmlFor="mv-makes" className="block text-sm font-medium text-neutral-700 mb-1">
                      {mvEvent === 'bud' || mvEvent === 'graft' ? 'Makes' : 'Becomes'}
                    </label>
                    <select id="mv-makes" value={mvItem} onChange={e => setMvItem(e.target.value ? Number(e.target.value) : '')}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                      <option value="">{mvEvent === 'bud' || mvEvent === 'graft' ? 'Choose the tree it makes…' : 'The same as this lot'}</option>
                      {makesOptions.map(i => <option key={i.id} value={i.id}>{itemName(i)}</option>)}
                    </select>
                    {makesOptions.length === 0 && (
                      <p className="text-xs text-amber-700 mt-1">
                        {mvEvent === 'bud' || mvEvent === 'graft'
                          ? 'None of your stock items is on this lot’s rootstock. Add one under Stock Items first.'
                          : 'No other stock item is this plant in another form. Add one under Stock Items to pot it up into it.'}
                      </p>
                    )}
                  </div>
                )}

                {methodOptions.length > 0 && mvEvent !== 'adjust' && !eventMeta.terminal && mvEvent !== 'grade' && (
                  <div>
                    <label htmlFor="mv-method" className="block text-sm font-medium text-neutral-700 mb-1">Method</label>
                    <select id="mv-method" value={mvMethod} onChange={e => setMvMethod(e.target.value ? Number(e.target.value) : '')}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                      <option value="">Not recorded</option>
                      {methodOptions.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                )}

                {eventMeta.containers && containers.length > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="mv-container" className="block text-sm font-medium text-neutral-700 mb-1">In</label>
                      <select id="mv-container" value={mvContainer}
                        onChange={e => setMvContainer(e.target.value ? Number(e.target.value) : '')}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">No trays</option>
                        {containers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    {chosenContainer && (
                      <div>
                        <label htmlFor="mv-container-count" className="block text-sm font-medium text-neutral-700 mb-1">How many</label>
                        <input id="mv-container-count" type="text" inputMode="numeric" value={mvContainerCount}
                          onChange={e => {
                            const n = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                            if (n !== '' && Number.isNaN(n)) return;
                            setMvContainerCount(n);
                            // Trays × plants per tray is the batch, until a number is typed.
                            if (n !== '' && mvQty === '') setMvQty(n * chosenContainer.cells_per_container);
                          }}
                          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    )}
                    {chosenContainer && mvContainerCount !== '' && (
                      <p className="col-span-2 text-xs text-neutral-500">
                        {mvContainerCount} × {chosenContainer.cells_per_container} = {(Number(mvContainerCount) * chosenContainer.cells_per_container).toLocaleString()} plants at most.
                      </p>
                    )}
                  </div>
                )}

                {(mvEvent === 'graft' || mvEvent === 'bud') && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Graft/bud wood source</label>
                    <input value={mvSource} onChange={e => setMvSource(e.target.value)}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" placeholder="supplier or donor reference" />
                  </div>
                )}

                {mvEvent === 'grade' && (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">1st grade</label>
                      <input type="number" min="0" value={mvFirst} onChange={e => setMvFirst(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">2nd grade</label>
                      <input type="number" min="0" value={mvSecond} onChange={e => setMvSecond(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Cull</label>
                      <input type="number" min="0" value={mvCull} onChange={e => setMvCull(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                  <input value={mvNotes} onChange={e => setMvNotes(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                </div>

                {saveError && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{saveError}</div>}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => { setShowMove(false); resetMove(); }}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={submitMove} disabled={saving}>
                  {saving ? 'Recording…' : 'Record'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default LotDetail;
